import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { hasStatus, isSdkStatus } from '../../../constants/status';
import { ANIMATION_DURATIONS } from '../../../constants/animations';
import {
  createStatusTransitionSequence,
} from '../utils/transitionTimings';

/**
 * Hook para gerenciar transições de status (loading, success, error, etc)
 *
 * Controla a sequência de animações ao entrar/sair de um estado de status, garantindo:
 * - Tempo mínimo de exibição (MIN_LOADING_DISPLAY = 2s)
 * - Sequência suave: fadeOut conteúdo → fadeIn status → fadeOut status → fadeIn conteúdo
 * - Estados intermediários para aplicar classes CSS corretas
 *
 * Fluxo ao ENTRAR em status (null → status):
 * 1. contentFadeOut = true (conteúdo desaparece)
 * 2. displayStatus = status (troca para GlobalProgress)
 * 3. contentFadeOut = false (GlobalProgress aparece)
 *
 * Fluxo ao SAIR de status (status → null):
 * 1. Aguarda GlobalProgress terminar fila (globalProgressBusy = false)
 * 2. statusForProgress = null (GlobalProgress desaparece)
 * 3. displayStatus = null, contentDelay = true, fromLoading = true (prepara conteúdo)
 * 4. contentDelay = false (conteúdo começa a aparecer com delay)
 * 5. fromLoading = false (transição completa)
 *
 * @param {Object|null} externalStatus - Status externo com { type: string, text: string }
 * @param {boolean} globalProgressBusy - Se o GlobalProgress está ocupado processando fila
 * @returns {Object} Estados para controle das transições
 * @returns {Object|null} returns.displayStatus - Status atualmente exibido (pode ser diferente do externo durante transições)
 * @returns {Object|null} returns.statusForProgress - Status a ser passado para o GlobalProgress
 * @returns {boolean} returns.contentFadeOut - Se o conteúdo deve fazer fadeOut (classe CSS: content-fade-out)
 * @returns {boolean} returns.contentDelay - Se o conteúdo deve ter delay antes de aparecer (classe CSS: content-delayed)
 * @returns {boolean} returns.fromLoading - Se está vindo de um estado de loading (classe CSS: from-loading)
 *
 * @example
 * const { displayStatus, statusForProgress, contentFadeOut, contentDelay, fromLoading }
 *   = useStatusTransition(status, globalProgressBusy);
 *
 * // displayStatus controla qual componente renderizar (GlobalProgress ou Router)
 * // statusForProgress é o status atual do GlobalProgress
 * // contentFadeOut, contentDelay, fromLoading são classes CSS para animações
 */
export const useStatusTransition = (externalStatus, globalProgressBusy) => {
  const [displayStatus, setDisplayStatus] = useState(externalStatus);
  const [statusForProgress, setStatusForProgress] = useState(externalStatus);
  const [contentFadeOut, setContentFadeOut] = useState(false);
  const [contentDelay, setContentDelay] = useState(false);
  const [fromLoading, setFromLoading] = useState(false);

  const statusStartTimeRef = useRef(null);
  const hasHadTransitionRef = useRef(false);
  const isTransitioningRef = useRef(false);
  const previousExternalStatusRef = useRef(externalStatus);
  const contentFadeOutTimeoutRef = useRef(null);

  // Usa useLayoutEffect para capturar mudanças ANTES do batching
  // Isso garante que cada mudança seja processada individualmente
  useLayoutEffect(() => {
    if (externalStatus && externalStatus !== previousExternalStatusRef.current) {
      previousExternalStatusRef.current = externalStatus;
      setStatusForProgress({ ...externalStatus, _timestamp: Date.now() });
    } else if (!externalStatus && previousExternalStatusRef.current) {
      previousExternalStatusRef.current = null;
      // Reseta statusForProgress imediatamente para GlobalProgress fazer fade-out
      setStatusForProgress(null);
    }
  }, [externalStatus]);

  useEffect(() => {
    const hasExternalStatus = hasStatus(externalStatus);
    const hasDisplayStatus = hasStatus(displayStatus);

    // Se está em transição, não faz nada (evita cancelar timeouts)
    if (isTransitioningRef.current) {
      return;
    }

    // Entrando em status (null -> status)
    if (hasExternalStatus && !hasDisplayStatus) {
      hasHadTransitionRef.current = true;
      statusStartTimeRef.current = Date.now();

      // Se é status SDK, não faz fade-out (mesmo conteúdo, apenas altura diferente)
      if (isSdkStatus(externalStatus)) {
        setDisplayStatus(externalStatus);
        return;
      }

      // Para outros status (loading, success), faz fade-out normal
      setContentFadeOut(true);

      // Cancela timeout anterior se existir
      if (contentFadeOutTimeoutRef.current) {
        clearTimeout(contentFadeOutTimeoutRef.current);
      }

      // Aguarda fade-out completar antes de setar displayStatus e limpar contentFadeOut
      contentFadeOutTimeoutRef.current = setTimeout(() => {
        setDisplayStatus(externalStatus);
        setContentFadeOut(false);
        contentFadeOutTimeoutRef.current = null;
      }, ANIMATION_DURATIONS.BASE);

      // Não retorna cleanup - queremos que o timeout complete
      return;
    }

    // Já tem status, atualiza display
    if (hasExternalStatus && hasDisplayStatus) {
      // Se está saindo de SDK para outro status, precisa fazer fade-out do conteúdo
      if (isSdkStatus(displayStatus) && !isSdkStatus(externalStatus)) {
        setContentFadeOut(true);

        // Cancela timeout anterior se existir
        if (contentFadeOutTimeoutRef.current) {
          clearTimeout(contentFadeOutTimeoutRef.current);
        }

        // Aguarda fade-out completar antes de setar displayStatus e limpar contentFadeOut
        contentFadeOutTimeoutRef.current = setTimeout(() => {
          setDisplayStatus(externalStatus);
          setContentFadeOut(false);
          contentFadeOutTimeoutRef.current = null;
        }, ANIMATION_DURATIONS.BASE);

        return;
      }

      // Para outros casos, apenas atualiza
      setDisplayStatus(externalStatus);
      return;
    }

    // Saindo de status (status -> null)
    if (!hasExternalStatus && hasDisplayStatus) {
      // Se está saindo de SDK, apenas limpa displayStatus (sem animações)
      if (isSdkStatus(displayStatus)) {
        setDisplayStatus(null);
        return;
      }

      // Se o GlobalProgress ainda está busy, aguarda terminar a fila
      if (globalProgressBusy) {
        setContentFadeOut(false);
        return;
      }

      // GlobalProgress terminou a fila, pode iniciar transição de saída
      setContentFadeOut(false);

      // Não usa tempo mínimo - o tempo já foi garantido pela fila de status
      // Cada status tem seu MIN_TEXT_DISPLAY, então o tempo total é automático
      const remainingTime = 0;

      isTransitioningRef.current = true;

      createStatusTransitionSequence(
        {
          onFadeOutStart: () => setStatusForProgress(null),
          onStatusHidden: () => {
            setDisplayStatus(null);
            setContentDelay(true);
            setFromLoading(true);
          },
          onContentDelayEnd: () => setContentDelay(false),
          onTransitionComplete: () => {
            setFromLoading(false);
            isTransitioningRef.current = false;
          },
        },
        remainingTime,
      );

      return;
    }

    // Reset inicial - apenas se nunca teve transição E ambos são null
    if (
      !hasExternalStatus &&
      !hasDisplayStatus &&
      !hasHadTransitionRef.current
    ) {
      setContentFadeOut(false);
      setContentDelay(false);
      setFromLoading(false);
    }
  }, [externalStatus, displayStatus, globalProgressBusy]);

  return {
    displayStatus,
    statusForProgress,
    contentFadeOut,
    contentDelay,
    fromLoading,
  };
};
